import { type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedArticles, RelatedServices } from "@/components/RelatedLinks";
import { SITE } from "@/lib/seo";
import jsonLd from "@/data/business-laptop-it-disposal-checklist-jsonld.json";

/**
 * Dedicated article route modelled on the certificate of destruction guide.
 * Copy is the approved article verbatim; JSON-LD is emitted exactly as
 * supplied in the data file.
 */

const SLUG = "business-laptop-it-disposal-checklist";
const URL = `${SITE}/blog/${SLUG}`;
const TITLE_TAG = "Business Laptop and IT Disposal Checklist | Preserve Earth";
const META_DESCRIPTION =
  "A step by step checklist for retiring business laptops and IT kit: leased devices, asset lists, data wiping or physical destruction, and records.";
const H1 = "Business laptop and IT equipment disposal: a practical checklist";
const HERO_SRC = "/images/blog/business-laptop-it-disposal-checklist-hero.webp";
const OG_IMAGE = `${SITE}/images/blog/business-laptop-it-disposal-checklist-og.jpg`;
const HERO_ALT =
  "IT team member packing closed laptops into a clear crate in a bright storeroom, with a tablet showing a blank checklist";
const DATE = "2026-11-04";

const crumbs = [
  { name: "Blog", path: "/blog" },
  { name: H1, path: `/blog/${SLUG}` },
];

const SOURCES = [
  "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/",
  "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-management-and-security",
  "https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/security/a-guide-to-data-security/",
  "https://ico.org.uk/for-organisations/report-a-breach/",
];

export const Route = createFileRoute("/blog/business-laptop-it-disposal-checklist")({
  head: () => ({
    meta: [
      { title: TITLE_TAG },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: TITLE_TAG },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: HERO_ALT },
      { property: "article:published_time", content: DATE },
      { property: "article:modified_time", content: DATE },
      { property: "article:section", content: "IT and Media Recycling" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE_TAG },
      { name: "twitter:description", content: META_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: HERO_ALT },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: (jsonLd as object[]).map((node) => ({
      type: "application/ld+json",
      children: JSON.stringify(node),
    })),
  }),
  component: BusinessLaptopItDisposalChecklistPage,
});

const linkClass =
  "underline decoration-moss/40 underline-offset-4 transition-colors hover:text-moss";
const h2Class = "mt-14 font-serif text-3xl leading-tight text-charcoal lg:text-4xl";
const h3Class = "mt-10 font-serif text-xl leading-snug text-moss";
const bodyClass = "mt-5 text-pretty text-base leading-[1.8] text-charcoal/80";
const listClass =
  "mt-6 list-disc space-y-3 pl-6 text-base leading-[1.7] text-charcoal/80 marker:text-moss";
const stepsClass =
  "mt-6 list-decimal space-y-3 pl-6 text-base leading-[1.7] text-charcoal/80 marker:text-moss";

function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className={linkClass}>
      {children}
    </a>
  );
}

function BusinessLaptopItDisposalChecklistPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="/#enquire"
      />

      <main>
        <article>
          <header className="container-page pt-16 pb-10">
            <Breadcrumbs crumbs={crumbs} />
            <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
              IT and Media Recycling · 6 min read
            </p>
            <h1 className="max-w-[28ch] text-balance font-serif text-4xl leading-[1.05] text-charcoal md:text-5xl lg:text-6xl">
              {H1}
            </h1>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-charcoal/70">
              By Preserve Earth team, Last updated <time dateTime={DATE}>4 November 2026</time>
            </p>
          </header>

          <div className="container-page">
            <figure className="overflow-hidden rounded-md outline outline-1 -outline-offset-1 outline-charcoal/10">
              <img
                src={HERO_SRC}
                alt={HERO_ALT}
                width={1280}
                height={720}
                loading="eager"
                fetchPriority="high"
                className="aspect-[16/9] w-full object-cover"
              />
            </figure>
          </div>

          <div className="container-page py-20">
            <div className="grid gap-16 lg:grid-cols-12">
              <aside className="lg:col-span-3">
                <div className="sticky top-24 space-y-6 border-l border-moss/30 pl-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    The Ledger
                  </p>
                  <p className="text-sm leading-relaxed text-charcoal/70">
                    Field notes on compliant, documented disposal for UK businesses.
                  </p>
                  <Link
                    to="/blog"
                    className="block text-xs font-medium uppercase tracking-[0.2em] text-moss underline decoration-moss/30 underline-offset-4"
                  >
                    All articles
                  </Link>
                </div>
              </aside>

              <div className="lg:col-span-9 lg:max-w-[68ch]">
                <p className="text-pretty text-lg leading-[1.75] text-charcoal">
                  <strong>Short answer:</strong> Before any laptop, desktop, server or phone leaves
                  your business, know exactly what it is, confirm you are allowed to dispose of it,
                  decide how the data will be dealt with, keep it secure until collection, and get
                  documentation back that lets you close it off on your asset list. The checklist
                  below walks through each step.
                </p>

                <p className={bodyClass}>
                  Old devices are easy to forget. They sit in a cupboard after a refresh, then a
                  desk move or office clear-out turns them into a pile no one wants to own. The data
                  on them does not go away while they wait. The ICO warns that if personal
                  information on electronic devices is not destroyed securely, it may be recoverable.
                </p>

                <section id="section-1" aria-labelledby="section-1-h">
                  <h2 id="section-1-h" className={h2Class}>
                    Step 1: Build or update your asset list
                  </h2>
                  <p className={bodyClass}>
                    Start with a list of everything you plan to retire. For each item record:
                  </p>
                  <ul className={listClass}>
                    <li>device type and make</li>
                    <li>serial number and any internal asset tag</li>
                    <li>who last used it, or which team</li>
                    <li>where it is now</li>
                    <li>whether it holds, or may hold, personal or confidential data</li>
                    <li>whether it is owned, leased or rented</li>
                  </ul>
                  <p className={bodyClass}>
                    Do not forget the things that are easy to miss: external drives, USB sticks, old
                    phones and tablets, network storage, and printers or copiers with internal
                    storage.
                  </p>
                  <p className={bodyClass}>
                    The ICO's{" "}
                    <A href="https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/">
                      disposal and deletion toolkit
                    </A>{" "}
                    expects a log of equipment sent for disposal and a log of devices awaiting
                    destruction and their location. Your asset list is the starting point for both.
                  </p>
                </section>

                <section id="section-2" aria-labelledby="section-2-h">
                  <h2 id="section-2-h" className={h2Class}>
                    Step 2: Check whether the devices are leased
                  </h2>
                  <p className={bodyClass}>
                    This is the step most often skipped. Many businesses lease or rent laptops,
                    phones and multifunction printers. If a device is leased, it is not yours to
                    destroy.
                  </p>
                  <ul className={listClass}>
                    <li>
                      Check your finance and leasing agreements, or ask your finance team or IT
                      provider.
                    </li>
                    <li>
                      Find out what the agreement says about return condition and data on returned
                      equipment.
                    </li>
                    <li>
                      If storage drives must be destroyed but the device must be returned, ask the
                      leasing company what it allows. Some agreements permit drive removal, others
                      do not.
                    </li>
                    <li>Keep leased devices separate from owned ones, physically and on your list.</li>
                  </ul>
                  <p className={bodyClass}>
                    Destroying a leased device by mistake can create a contractual problem. Returning
                    one with data still on it creates a data protection one.
                  </p>
                </section>

                <section id="section-3" aria-labelledby="section-3-h">
                  <h2 id="section-3-h" className={h2Class}>
                    Step 3: Decide between data wiping and physical destruction
                  </h2>
                  <p className={bodyClass}>
                    There are two broad routes. The ICO lists secure methods such as device wiping,
                    degaussing and hardware shredding.
                  </p>
                  <h3 className={h3Class}>Data wiping</h3>
                  <p className={bodyClass}>
                    Wiping uses software to overwrite or securely erase the storage so the data
                    cannot be recovered, leaving the device usable.
                  </p>
                  <ul className={listClass}>
                    <li>Suits devices that will be returned, reused or resold.</li>
                    <li>Needs to be done with a suitable method for the storage type, and verified.</li>
                    <li>You should get a record per device, showing serial number and result.</li>
                  </ul>
                  <h3 className={h3Class}>Physical destruction</h3>
                  <p className={bodyClass}>
                    Physical destruction means the storage is destroyed, for example by shredding
                    hard drives and solid state drives.
                  </p>
                  <ul className={listClass}>
                    <li>
                      Suits drives that have failed, cannot be wiped reliably, or held highly
                      sensitive data.
                    </li>
                    <li>Leaves nothing to recover, but the device or drive cannot be reused.</li>
                  </ul>
                  <h3 className={h3Class}>Which one?</h3>
                  <p className={bodyClass}>
                    Many businesses use both: wipe what can be returned or reused, destroy what
                    cannot. Make the decision per device and write it on your asset list so the
                    reason is recorded. If you are not sure, treat the device as needing destruction.
                  </p>
                </section>

                <section id="section-4" aria-labelledby="section-4-h">
                  <h2 id="section-4-h" className={h2Class}>
                    Step 4: Remove accounts and settings, then lock it away
                  </h2>
                  <p className={bodyClass}>Before collection:</p>
                  <ul className={listClass}>
                    <li>remove devices from device management, accounts and remote access tools</li>
                    <li>
                      record any device that will not power on, as it cannot be wiped and should be
                      flagged for destruction
                    </li>
                    <li>remove SIM and memory cards and log them</li>
                    <li>store everything in a locked room or cabinet with restricted access</li>
                  </ul>
                  <p className={bodyClass}>
                    The ICO expects devices awaiting destruction to be stored securely, for example
                    in a locked area with restricted access, with a log of what is there. Keep the
                    key holder list short.
                  </p>
                </section>

                <section id="section-5" aria-labelledby="section-5-h">
                  <h2 id="section-5-h" className={h2Class}>
                    Step 5: Watch out for batteries
                  </h2>
                  <p className={bodyClass}>
                    Laptops, phones and tablets contain lithium batteries. They should never go in
                    general waste or recycling bins. Keep them dry and undamaged until collection. We
                    covered why in our post on{" "}
                    <A href="https://preserveearth.co.uk/blog/office-laptops-lithium-batteries-safe-disposal">
                      office laptops and lithium batteries
                    </A>.
                  </p>
                </section>

                <section id="section-6" aria-labelledby="section-6-h">
                  <h2 id="section-6-h" className={h2Class}>
                    Step 6: Choose how you will hand over the equipment
                  </h2>
                  <p className={bodyClass}>
                    Decide whether this is a one-off clear-out or part of a regular cycle. A single
                    collection after an upgrade suits{" "}
                    <A href="https://preserveearth.co.uk/services/one-off-shredding">
                      one-off shredding
                    </A>, which covers hard drives, PCs and servers as well as paper. Regular refreshes
                    may suit a scheduled service through our{" "}
                    <A href="https://preserveearth.co.uk/services/it-and-media-recycling">
                      IT and media recycling
                    </A>{" "}
                    team.
                  </p>
                  <p className={bodyClass}>
                    Whoever you use, the ICO's{" "}
                    <A href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/security/a-guide-to-data-security/">
                      guide to data security
                    </A>{" "}
                    expects appropriate security measures, and that extends to the people handling
                    your devices on your behalf. Agree in writing what will happen to each device.
                  </p>
                </section>

                <section id="section-7" aria-labelledby="section-7-h">
                  <h2 id="section-7-h" className={h2Class}>
                    Step 7: Get documentation back and close the loop
                  </h2>
                  <p className={bodyClass}>
                    When devices leave, you need evidence of what happened to each one. For IT and
                    media we provide chain of custody and destruction documentation, so you have an
                    audit trail for your records.
                  </p>
                  <ul className={listClass}>
                    <li>Match the documentation against your asset list, ideally by serial number.</li>
                    <li>Mark each item as returned, wiped or destroyed, with the date and reference.</li>
                    <li>
                      Store the paperwork with your other disposal records. Our{" "}
                      <A href="https://preserveearth.co.uk/blog/certificate-of-destruction-guide">
                        certificate of destruction guide
                      </A>{" "}
                      explains what to look for.
                    </li>
                  </ul>
                </section>

                <section id="section-8" aria-labelledby="section-8-h">
                  <h2 id="section-8-h" className={h2Class}>
                    Step 8: Know what to do if something goes missing
                  </h2>
                  <p className={bodyClass}>
                    If a device that held personal data cannot be found, treat it as a possible
                    personal data breach. The ICO explains{" "}
                    <A href="https://ico.org.uk/for-organisations/report-a-breach/">
                      when and how to report a breach
                    </A>. Where reporting is required, it must be within 72 hours of becoming aware of
                    it.
                  </p>
                </section>

                <section id="section-9" aria-labelledby="section-9-h">
                  <h2 id="section-9-h" className={h2Class}>
                    The checklist at a glance
                  </h2>
                  <ol className={stepsClass}>
                    <li>List every device with serial number, user and location.</li>
                    <li>Mark each as owned, leased or rented.</li>
                    <li>Check leasing terms before touching leased kit.</li>
                    <li>Decide wipe or destroy per device, and record why.</li>
                    <li>Remove from accounts and device management.</li>
                    <li>Store securely with restricted access and a log.</li>
                    <li>Keep batteries out of the bin.</li>
                    <li>Book collection and agree what will happen to each item.</li>
                    <li>Match documentation to your list and close each item off.</li>
                    <li>Escalate anything missing straight away.</li>
                  </ol>
                </section>

                <section id="faq" aria-labelledby="faq-h">
                  <h2 id="faq-h" className={h2Class}>
                    Frequently asked questions
                  </h2>
                  <div className="mt-8 divide-y divide-charcoal/10 border-y border-charcoal/10">
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Can we dispose of leased laptops ourselves?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Not without checking your leasing agreement. A leased device belongs to the
                        leasing company, so ask what it requires before any wiping, drive removal or
                        destruction.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Is wiping a laptop enough, or should we destroy the hard drive?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Both can be secure when done properly. Wiping suits devices that will be
                        returned or reused. Physical destruction suits failed drives, drives that
                        cannot be wiped reliably, or very sensitive data. Record the decision for
                        each device.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        What should our IT asset list include before disposal?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Device type, serial number, asset tag, last user, current location, whether
                        it holds personal data and whether it is owned or leased.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        What paperwork should we receive after IT disposal?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Documentation that lets you match each device to what happened to it. For IT
                        and media we provide chain of custody and destruction documentation for your
                        records.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Do printers and copiers need the same care?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Some printers and multifunction devices have internal storage. Check with the
                        supplier or leasing company and add them to your asset list.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        What if a device goes missing before collection?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Treat it as a possible personal data breach and check the ICO guidance. Where
                        a breach must be reported, it has to be reported within 72 hours of becoming
                        aware of it.
                      </p>
                    </div>
                  </div>
                </section>

                <aside
                  aria-labelledby="cta-h"
                  className="mt-20 border-y border-charcoal/15 bg-sage-light/40 px-8 py-10"
                >
                  <h2 id="cta-h" className="font-serif text-2xl text-charcoal">
                    Retiring laptops or IT equipment?
                  </h2>
                  <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-charcoal/70">
                    Send us your list and we will suggest the right collection option and the records
                    you will receive.
                  </p>
                  <a
                    href="https://preserveearth.co.uk/services/it-and-media-recycling#enquire"
                    className="mt-6 inline-flex items-center gap-3 rounded-sm bg-moss px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-charcoal"
                  >
                    Get in touch
                  </a>
                </aside>

                <div className="mt-12 text-sm leading-relaxed text-charcoal/70">
                  <h2 className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    Sources
                  </h2>
                  <ul className="mt-3 space-y-2">
                    {SOURCES.map((href) => (
                      <li key={href}>
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener"
                          className={`${linkClass} break-all`}
                        >
                          {href}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </article>

        <RelatedArticles
          slugs={[
            "office-laptops-lithium-batteries-safe-disposal",
            "certificate-of-destruction-guide",
          ]}
        />
        <RelatedServices
          slugs={["it-and-media-recycling", "one-off-shredding", "confidential-waste-disposal"]}
        />
      </main>

      <SiteFooter />
    </div>
  );
}
